import { ReactNode } from 'react';

import { Text } from '@growtherapy/sprout-ui';

import { IntakeFormConditionalCheckboxGroupAnswer } from './IntakeFormConditionalCheckboxGroupAnswer';
import {
  INTAKE_ANSWER_TEXT_CLASSNAME,
  INTAKE_ANSWER_TEXT_CLASSNAME_COMPACT,
  INTAKE_MASKED_ANSWER_TEXT_CLASSNAME,
  INTAKE_UNANSWERED_FALLBACK,
  IntakeFormValues,
  formatPrimaryCarePhysician,
  formatSelectedOptionLabels,
  getSelectedValues,
  isOptionalUnansweredIntakeQuestion,
} from './intakeFormAnswerUtils';
import {
  AnswerValue,
  ContactValue,
  GroupedResponse,
  MedicationValue,
  PrimaryCarePhysicianValue,
  ProfessionalResourceValue,
  QuestionTypeEnum,
} from './types';

type IntakeFormAnswerProps = {
  questionWithAnswer: GroupedResponse;
  formValues?: IntakeFormValues;
};

function MaskedAnswerText({ children }: { children: ReactNode }) {
  return (
    <Text data-dd-privacy='mask' className={INTAKE_MASKED_ANSWER_TEXT_CLASSNAME}>
      {children}
    </Text>
  );
}

function formatInlineOptionAnswer(questionWithAnswer: GroupedResponse, answer: AnswerValue): string {
  const selectedValues = getSelectedValues(answer);
  return formatSelectedOptionLabels(questionWithAnswer.questionElement?.options, selectedValues);
}

function formatMultiSelectAnswer(answer: AnswerValue): string {
  return getSelectedValues(answer).join(', ');
}

function IntakeFormFallbackAnswer() {
  return <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{INTAKE_UNANSWERED_FALLBACK}</Text>;
}

export function IntakeFormAnswer({ questionWithAnswer, formValues = {} }: IntakeFormAnswerProps) {
  if (isOptionalUnansweredIntakeQuestion(questionWithAnswer)) {
    return <IntakeFormFallbackAnswer />;
  }

  const answer = questionWithAnswer.answer;

  if (answer === 'null' || answer === undefined) {
    return null;
  }

  switch (questionWithAnswer.type) {
    case QuestionTypeEnum.SECTION_TITLE:
    case QuestionTypeEnum.DIVIDER:
      return null;
    case QuestionTypeEnum.SELECT:
      return <Text className={INTAKE_ANSWER_TEXT_CLASSNAME_COMPACT}>{answer as string}</Text>;
    case QuestionTypeEnum.RADIO:
    case QuestionTypeEnum.LIKERT_SCALE:
    case QuestionTypeEnum.TEXT_WITH_SUGGESTIONS:
      return (
        <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>
          {formatInlineOptionAnswer(questionWithAnswer, answer) || (answer as string)}
        </Text>
      );
    case QuestionTypeEnum.TEXT:
    case QuestionTypeEnum.TEXT_AREA:
      return <MaskedAnswerText>{answer as string}</MaskedAnswerText>;
    case QuestionTypeEnum.CHOSEN_NAME_INPUT:
    case QuestionTypeEnum.PHONE_NUMBER_INPUT:
      return <MaskedAnswerText>{answer as string}</MaskedAnswerText>;
    case QuestionTypeEnum.MEDICATION_INPUT:
    case QuestionTypeEnum.MEDICATION_INPUT_WITH_SUGGESTIONS:
      return (
        <>
          {(answer as MedicationValue[]).map((medication, index) => (
            <div className='pt-2 pb-2' key={`${medication.medicationName}-${index}`}>
              <MaskedAnswerText>Medication: {medication.medicationName}</MaskedAnswerText>
              <MaskedAnswerText>Dosage: {medication.dosage}</MaskedAnswerText>
            </div>
          ))}
        </>
      );
    case QuestionTypeEnum.PRIMARY_CARE_PHYSICIAN_INPUT:
      return <MaskedAnswerText>{formatPrimaryCarePhysician(answer as PrimaryCarePhysicianValue)}</MaskedAnswerText>;
    case QuestionTypeEnum.EMERGENCY_CONTACT_INPUT:
      return (
        <>
          {(answer as ContactValue[]).map((contact, index) => (
            <div key={contact.name || contact.phoneNumber || index}>
              {contact.name && <MaskedAnswerText>Name: {contact.name}</MaskedAnswerText>}
              {contact.phoneNumber && <MaskedAnswerText>Phone Number: {contact.phoneNumber}</MaskedAnswerText>}
            </div>
          ))}
        </>
      );
    case QuestionTypeEnum.PROFESSIONAL_RESOURCE_INPUT: {
      const resourceAnswer = answer as ProfessionalResourceValue;
      return (
        <>
          {resourceAnswer.address && <MaskedAnswerText>Address: {resourceAnswer.address}</MaskedAnswerText>}
          {resourceAnswer.phoneNumber && (
            <MaskedAnswerText>Phone Number: {resourceAnswer.phoneNumber}</MaskedAnswerText>
          )}
          {resourceAnswer.resources?.map((resource, index) => (
            <div key={resource.name ?? resource.phoneNumber ?? index}>
              {resource.name && <MaskedAnswerText>Name: {resource.name}</MaskedAnswerText>}
              {resource.phoneNumber && <MaskedAnswerText>Phone Number: {resource.phoneNumber}</MaskedAnswerText>}
            </div>
          ))}
        </>
      );
    }
    case QuestionTypeEnum.CHECKBOX:
    case QuestionTypeEnum.MULTI_SELECT:
      return <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{formatMultiSelectAnswer(answer)}</Text>;
    case QuestionTypeEnum.MULTI_SELECT_PILL:
      return (
        <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{formatInlineOptionAnswer(questionWithAnswer, answer)}</Text>
      );
    case QuestionTypeEnum.CONDITIONAL_CHECKBOX_GROUP:
      return (
        <IntakeFormConditionalCheckboxGroupAnswer formValues={formValues} questionWithAnswer={questionWithAnswer} />
      );
    case QuestionTypeEnum.COMPLEX_ERROR:
      return (
        <Text className='text-small rebrand:text-status-error-text'>{questionWithAnswer.question ?? 'Error'}</Text>
      );
    default:
      return <Text className={INTAKE_ANSWER_TEXT_CLASSNAME}>{String(answer)}</Text>;
  }
}

export default IntakeFormAnswer;
